import React, { Suspense } from 'react';
import { Control, FieldErrors, UseFormSetValue, UseFormWatch } from 'react-hook-form';
import {
    CheckCircleIcon,
    ExclamationTriangleIcon,
    DocumentTextIcon,
    HomeIcon,
    IdentificationIcon,
    CreditCardIcon,
    UserGroupIcon,
    HeartIcon,
    GiftIcon,
    FlagIcon
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolid, UserIcon as UserSolid, IdentificationIcon as IdentificationSolid } from '@heroicons/react/24/solid';
import { UpdateStatusEntry } from '@/features/Collaborators/Models/collaborator';
import { CollaboratorFormData } from '@/features/Collaborators/Edit/Types';
import { CollaboratorFormContext } from "@/features/Collaborators/Edit/Contexts/CollaboratorFormContext";
import {
    InstructionStep,
    PersonalDataStep,
    DocumentsStep,
    BankDetailsStep,
    AddressStep,
    HealthStep,
    FamilyStep,
    AboutYouStep,
    AlreadyUpdateStep,
    FinalStep
} from '@/features/Collaborators/Edit/Form/Steps';
import { CollaboratorInfoHeader } from '@/features/Collaborators/Shared/CollaboratorInfoHeader';

interface ProfileUpdateWizardStepperProps {
    updateStatus?: UpdateStatusEntry | null;
    currentStepOverride?: number; // Para sobrescrever o step atual quando controlado externamente
    context: CollaboratorFormContext;
    collaboratorId: string;
    collaboratorName: string;
    collaboratorEmail: string;
    collaboratorPhotoUrl?: string | null;
    control: Control<CollaboratorFormData>;
    setValue: UseFormSetValue<CollaboratorFormData>;
    watch: UseFormWatch<CollaboratorFormData>;
    errors: FieldErrors<CollaboratorFormData>;
    isSubmitting?: boolean;
    navigationButtons?: React.ReactNode;
    onNoBmgAccountChange?: (hasNoBmgAccount: boolean) => void;
}

// Definição dos steps do timeline
const timelineSteps = [
    { id: 'instruction', name: 'Instruções', icon: ExclamationTriangleIcon },
    { id: 'personal', name: 'Dados Pessoais', icon: IdentificationIcon },
    { id: 'address', name: 'Endereço', icon: HomeIcon },
    { id: 'documents', name: 'Documentos', icon: DocumentTextIcon },
    { id: 'banking', name: 'Dados Bancários', icon: CreditCardIcon },
    { id: 'family', name: 'Família', icon: UserGroupIcon },
    { id: 'health', name: 'Saúde', icon: HeartIcon },
    { id: 'about', name: 'Sobre Você', icon: GiftIcon },
    { id: 'final', name: 'Finalização', icon: FlagIcon },
];

const ProfileUpdateWizardStepper: React.FC<ProfileUpdateWizardStepperProps> = ({
    updateStatus,
    currentStepOverride,
    context,
    collaboratorId,
    collaboratorName,
    collaboratorEmail,
    collaboratorPhotoUrl,
    control,
    setValue,
    watch,
    errors,
    isSubmitting = false,
    navigationButtons,
    onNoBmgAccountChange
}) => {
    // Função para obter o step atual (usado consistentemente)
    const getCurrentStepActual = () => {
        if (currentStepOverride !== undefined) {
            return currentStepOverride;
        }

        const status = updateStatus?.byUser?.status ?? 'PENDING';

        if (status === 'PENDING') {
            return -1; // InstructionStep
        } else if (status === 'UPDATED') {
            return 8; // AlreadyUpdateStep
        } else if (status === 'IN_PROGRESS') {
            return (updateStatus?.byUser as any)?.currentStep ?? 0;
        }

        return -1; // Default para InstructionStep
    };

    const currentStepActual = getCurrentStepActual();

    // Determina o step atual baseado no override ou UpdateStatusEntry
    const getCurrentStepIndex = () => {
        // Mapear step atual para índice do timeline:
        // -1 (Instruções) -> 0 (primeiro item do timeline)
        // 0 (PersonalData) -> 1 (segundo item do timeline)
        // 1 (Address) -> 2 (terceiro item do timeline)
        // etc.
        // 8 (AlreadyUpdate) -> -1 (especial, todos completos)

        if (currentStepActual === -1) return 0; // Instruções = primeiro item do timeline
        if (currentStepActual === 8) return -1; // AlreadyUpdateStep (especial, todos completos)
        if (currentStepActual >= 0 && currentStepActual <= 7) {
            return currentStepActual + 1; // Step 0 = timeline[1], Step 1 = timeline[2], etc.
        }

        return 0; // Default para Instruções
    };

    const currentStepIndex = getCurrentStepIndex();

    // Função para determinar o status de cada step no timeline
    const getStepStatus = (stepIndex: number) => {
        // Quando o usuário já atualizou anteriormente (AlreadyUpdateStep)
        if (currentStepIndex === -1) {
            return 'complete';
        }

        // Se estamos exibindo o FinalStep, marcar todos como completos
        if (currentStepIndex === timelineSteps.length - 1) {
            return stepIndex <= currentStepIndex ? 'complete' : 'upcoming';
        }

        if (stepIndex < currentStepIndex) {
            return 'complete';
        }

        if (stepIndex === currentStepIndex) {
            return 'current';
        }

        return 'upcoming';
    };

    // Função para obter o nome da etapa atual
    const getCurrentStepName = () => {
        if (currentStepActual === -1) return "Instruções";
        if (currentStepActual === 8) return "Cadastro Atualizado";
        if (currentStepActual >= 0 && currentStepActual <= 6) {
            // Mapear step 0 para timelineSteps[1], step 1 para timelineSteps[2], etc.
            return timelineSteps[currentStepActual + 1]?.name || "Instruções";
        }
        if (currentStepActual === 7) return "Finalização";
        return "Instruções";
    };

    // Função para obter o número atual do passo (para exibição)
    const getCurrentStepNumber = () => {
        if (currentStepActual === -1) return { current: 1, total: 9 }; // Instruções = 1/9
        if (currentStepActual === 8) return { current: 9, total: 9 }; // Completo = 9/9
        if (currentStepActual >= 0 && currentStepActual <= 6) {
            // Step 0 = 2/9, Step 1 = 3/9, etc.
            return { current: currentStepActual + 2, total: 9 };
        }
        if (currentStepActual === 7) return { current: 9, total: 9 }; // Finalização = 9/9
        return { current: 1, total: 9 };
    };

    // Componente do step atual
    const getCurrentStep = () => {
        // Renderizar step baseado no currentStepActual
        if (currentStepActual === -1) {
            return <InstructionStep />;
        }

        if (currentStepActual === 8) {
            return <AlreadyUpdateStep />;
        }

        switch (currentStepActual) {
            case 0:
                return (
                    <PersonalDataStep
                        context={context}
                        collaboratorId={collaboratorId}
                        control={control}
                        errors={errors}
                        isSubmitting={isSubmitting}
                    />
                );
            case 1:
                return (
                    <AddressStep
                        context={context}
                        collaboratorId={collaboratorId}
                        control={control}
                        setValue={setValue}
                        watch={watch}
                        errors={errors}
                        isSubmitting={isSubmitting}
                    />
                );
            case 2:
                return (
                    <DocumentsStep
                        context={context}
                        collaboratorId={collaboratorId}
                        control={control}
                        setValue={setValue}
                        watch={watch}
                        errors={errors}
                        isSubmitting={isSubmitting}
                    />
                );
            case 3:
                return (
                    <BankDetailsStep
                        context={context}
                        collaboratorId={collaboratorId}
                        control={control}
                        setValue={setValue}
                        watch={watch}
                        errors={errors}
                        isSubmitting={isSubmitting}
                        onNoBmgAccountChange={onNoBmgAccountChange}
                    />
                );
            case 4:
                return (
                    <FamilyStep
                        context={context}
                        collaboratorId={collaboratorId}
                        control={control}
                        setValue={setValue}
                        watch={watch}
                        errors={errors}
                        isSubmitting={isSubmitting}
                    />
                );
            case 5:
                return (
                    <HealthStep
                        context={context}
                        collaboratorId={collaboratorId}
                        control={control}
                        setValue={setValue}
                        watch={watch}
                        errors={errors}
                        isSubmitting={isSubmitting}
                    />
                );
            case 6:
                return (
                    <AboutYouStep
                        context={context}
                        collaboratorId={collaboratorId}
                        control={control}
                        errors={errors}
                        isSubmitting={isSubmitting}
                    />
                );
            case 7:
                return <FinalStep />;
            default:
                return <InstructionStep />;
        }
    };

    return (
        <div className="max-w-7xl mx-auto min-h-screen flex flex-col">
            <div className="bg-white rounded-lg shadow flex-1 flex flex-col md:flex-row overflow-hidden">

                {/* Timeline Desktop - Primeira coluna (lateral) */}
                <div className="hidden md:flex md:w-80 md:flex-shrink-0 md:border-r md:border-gray-200 md:bg-gray-50 md:flex-col md:overflow-hidden">
                    {/* Timeline com rolagem */}
                    <div className="flex-1 overflow-y-auto">
                        <div className="p-6">
                            <nav aria-label="Progress">
                                <ol className="space-y-6">
                                    {timelineSteps.map((step, stepIdx) => {
                                        const status = getStepStatus(stepIdx);
                                        const Icon = step.icon;

                                        return (
                                            <li key={step.id} className="relative">
                                                {stepIdx !== timelineSteps.length - 1 && (
                                                    <div
                                                        className={`absolute left-4 top-8 -ml-px mt-0.5 h-6 w-0.5 ${status === 'complete' ? 'bg-indigo-600' : 'bg-gray-300'
                                                            }`}
                                                        aria-hidden="true"
                                                    />
                                                )}
                                                <div className="group relative flex items-center cursor-default select-none">
                                                    <span className="flex h-8 w-8 items-center justify-center flex-shrink-0">
                                                        {status === 'complete' ? (
                                                            <CheckCircleIcon
                                                                className="h-8 w-8 text-indigo-600"
                                                                aria-hidden="true"
                                                            />
                                                        ) : status === 'current' ? (
                                                            <div className="relative">
                                                                <div className="h-8 w-8 rounded-full border-2 border-indigo-600 bg-white flex items-center justify-center z-10 relative">
                                                                    <Icon className="h-4 w-4 text-indigo-600" />
                                                                </div>
                                                                <div className="absolute -inset-1 rounded-full animate-pulse bg-indigo-200 opacity-60" />
                                                            </div>
                                                        ) : (
                                                            <div className="h-8 w-8 rounded-full border-2 border-gray-300 bg-white flex items-center justify-center group-hover:border-gray-400">
                                                                <Icon className="h-4 w-4 text-gray-500 group-hover:text-gray-900" />
                                                            </div>
                                                        )}
                                                    </span>
                                                    <span className="ml-3 min-w-0 flex flex-col justify-center">
                                                        <span className={`text-sm font-medium ${status === 'complete'
                                                            ? 'text-indigo-600'
                                                            : status === 'current'
                                                                ? 'text-indigo-600'
                                                                : 'text-gray-500'
                                                            }`}>
                                                            {step.name}
                                                        </span>
                                                        {status === 'current' && (
                                                            <span className="text-xs text-indigo-600 font-medium">
                                                                Em andamento
                                                            </span>
                                                        )}
                                                        {status === 'complete' && (
                                                            <span className="text-xs text-gray-500">
                                                                Concluído
                                                            </span>
                                                        )}
                                                    </span>
                                                </div>
                                            </li>
                                        );
                                    })}
                                </ol>
                            </nav>
                        </div>
                    </div>
                </div>

                {/* Conteúdo - Segunda coluna */}
                <div className="flex-1 min-w-0 flex flex-col overflow-hidden">
                    {/* Header do colaborador */}
                    <div className="border-b border-gray-200 flex-shrink-0">
                        <CollaboratorInfoHeader
                            photoUrl={collaboratorPhotoUrl}
                            name={collaboratorName}
                            email={collaboratorEmail}
                            backgroundColor="bg-white"
                        />
                    </div>

                    {/* Indicador de progresso mobile - abaixo do header */}
                    <div className="md:hidden bg-indigo-50 border-b border-indigo-200 px-4 py-3 flex-shrink-0">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                                <div className="w-3 h-3 bg-indigo-600 rounded-full flex-shrink-0"></div>
                                <h2 className="text-sm font-semibold text-gray-900">
                                    {getCurrentStepName()}
                                </h2>
                            </div>
                            <div className="text-sm text-indigo-600 font-bold bg-white px-2 py-1 rounded-full border border-indigo-200">
                                {(() => {
                                    const { current, total } = getCurrentStepNumber();
                                    return `${current}/${total}`;
                                })()}
                            </div>
                        </div>
                        {/* Barra de progresso */}
                        <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
                            <div
                                className="bg-indigo-600 h-2 rounded-full transition-all duration-300"
                                style={{
                                    width: `${(() => {
                                        const { current, total } = getCurrentStepNumber();
                                        return (current / total) * 100;
                                    })()}%`
                                }}
                            ></div>
                        </div>
                    </div>

                    {/* Conteúdo do step atual com rolagem */}
                    <div className="flex-1 overflow-y-auto">
                        <Suspense fallback={
                            <div className="flex items-center justify-center py-12">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                            </div>
                        }>
                            {getCurrentStep()}
                        </Suspense>
                    </div>

                    {/* Barra de navegação fixa no final */}
                    {navigationButtons && (
                        <div className="border-t border-gray-200 bg-gray-50 px-4 py-3 sm:px-6 sm:py-4 flex-shrink-0">
                            <div className="flex justify-between items-center space-x-3">
                                {navigationButtons}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProfileUpdateWizardStepper; 